<template>
  <div
    class="v-select-basic"
    :class="{ 'v-select-basic--active': isOpen }"
    v-click-outside="closeDropdown"
    role="combobox"
    :aria-expanded="isOpen.toString()"
    aria-haspopup="listbox"
    :aria-owns="listboxId"
    :aria-controls="listboxId"
    :aria-label="selectorLabel"
    tabindex="0"
    @click.self="toggleDropdown"
    @keydown.enter.prevent="onEnterSpace"
    @keydown.space.prevent="onEnterSpace"
    @keydown.down.prevent="onArrowDown"
    @keydown.up.prevent="onArrowUp"
    @keydown.esc="closeDropdown"
  >
    <div
      class="v-select-basic__label"
      :class="{
        'v-select-basic__label--float': selectedOption || isOpen,
        'v-select-basic__label--float--active': isOpen
      }"
      aria-hidden="true"
    >
      {{ selectorLabel }}
    </div>
    <div class="v-select-basic__selected-option" aria-hidden="true">
      {{ selectedOption ? selectedOption.label : "" }}
    </div>
    <v-icon
      class="v-select-basic__icon"
      name="arrow-key-down"
      width="24"
      aria-hidden="true"
    />
    <div
      class="v-select-basic__dropdown"
      :class="{
        'v-select-basic__dropdown--mobile': isMobile && !mobileTakeover,
        'v-select-basic__dropdown--mobile-takeover': isMobile && mobileTakeover,
        'v-select-basic__dropdown--desktop': !isMobile
      }"
      v-show="isOpen"
      @click.self="toggleDropdown"
    >
      <span
        class="v-select-basic__dropdown__close"
        v-if="isMobile && mobileTakeover"
        role="button"
        tabindex="0"
        @click="closeDropdown"
        @keydown.enter.prevent="closeDropdown"
        @keydown.space.prevent="closeDropdown"
      >
        Luk
      </span>
      <ul
        :id="listboxId"
        class="v-select-basic__options"
        :class="{
          'v-select-basic__options--mobile': isMobile && !mobileTakeover,
          'v-select-basic__options--mobile-takeover':
            isMobile && mobileTakeover,
          'v-select-basic__options--desktop': !isMobile
        }"
        role="listbox"
        :aria-label="selectorLabel"
        :aria-activedescendant="activeDescendant"
      >
        <li
          v-for="(option, index) in options"
          class="v-select-basic__option"
          :class="{
            'v-select-basic__option--selected':
              option.value === (selectedOption ? selectedOption.value : value),
            'v-select-basic__option--focused': focusedIndex === index
          }"
          :key="option.value"
          :id="`${listboxId}-option-${index}`"
          role="option"
          :aria-selected="
            (
              option.value === (selectedOption ? selectedOption.value : value)
            ).toString()
          "
          @click="selectOption(option)"
        >
          {{ option.label }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "VSelectBasic",
  props: {
    label: String,
    options: Array,
    selectorLabel: String,
    value: [String, Object],
    initialValue: [String, Object],
    mobileTakeover: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const uid = Math.random()
      .toString(36)
      .substring(2, 11);
    return {
      isOpen: false,
      selectedOption: null,
      mobileBreakpoint: 768,
      windowWidth: window.innerWidth,
      focusedIndex: -1,
      listboxId: `v-select-basic-listbox-${uid}`
    };
  },
  created() {
    window.addEventListener("resize", this.updateWindowWidth);
    this.initializeSelectedOption();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateWindowWidth);
  },
  computed: {
    isMobile() {
      return this.windowWidth < this.mobileBreakpoint;
    },
    activeDescendant() {
      if (this.focusedIndex >= 0) {
        return `${this.listboxId}-option-${this.focusedIndex}`;
      }
      return null;
    }
  },
  methods: {
    initializeSelectedOption() {
      if (this.initialValue) {
        this.selectedOption = this.options.find(
          option => option.value === this.initialValue
        );
        if (this.selectedOption) {
          this.$emit("input", this.selectedOption.value);
        }
      } else if (this.value) {
        this.selectedOption = this.options.find(
          option => option.value === this.value
        );
      }
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.focusedIndex = this.getSelectedIndex();
      } else {
        this.focusedIndex = -1;
      }
    },
    closeDropdown() {
      this.isOpen = false;
      this.focusedIndex = -1;
    },
    selectOption(option) {
      this.selectedOption = option;
      this.$emit("input", option.value);
      this.closeDropdown();
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },
    getSelectedIndex() {
      if (!this.selectedOption) return 0;
      const idx = this.options.findIndex(
        o => o.value === this.selectedOption.value
      );
      return idx >= 0 ? idx : 0;
    },
    onArrowDown() {
      if (!this.isOpen) {
        this.isOpen = true;
        this.focusedIndex = this.getSelectedIndex();
        return;
      }
      if (this.focusedIndex < this.options.length - 1) {
        this.focusedIndex++;
      }
    },
    onArrowUp() {
      if (!this.isOpen) {
        this.isOpen = true;
        this.focusedIndex = this.getSelectedIndex();
        return;
      }
      if (this.focusedIndex > 0) {
        this.focusedIndex--;
      }
    },
    onEnterSpace() {
      if (!this.isOpen) {
        this.toggleDropdown();
      } else if (
        this.focusedIndex >= 0 &&
        this.focusedIndex < this.options.length
      ) {
        this.selectOption(this.options[this.focusedIndex]);
      } else {
        this.closeDropdown();
      }
    }
  },
  directives: {
    clickOutside: {
      bind: function(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.addEventListener("click", el.clickOutsideEvent);
      },
      unbind: function(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";
@import "./v-select-basic.scss";
</style>
